import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import type { ProductSubcategory } from "@/lib/products";

// ---------------------------------------------------------------------------
// SelectSubcategoryDialog — modal-over-embedded subcategory picker.
//
// Layout matches `Select Subcategory popup.png`:
//
//   ┌──────────────────────────┐
//   │ Select subcategory       │
//   ├──────────────────────────┤
//   │ [ Subcategory 1       ]  │
//   │ [ Subcategory 2       ]  │
//   │ [ ...                 ]  │
//   ├──────────────────────────┤
//   │                  [Close] │
//   └──────────────────────────┘
//
// The dialog overlays `ProductPicker`, which stays mounted underneath. Picking
// a subcategory closes the dialog and lets the parent push the chosen id into
// `categoryId` for the embedded category list.
// ---------------------------------------------------------------------------

export interface SelectSubcategoryDialogProps {
  /** Open/closed state — controlled by the parent. */
  open: boolean;
  /** Open-change handler. The dialog also calls this with `false` after a pick. */
  onOpenChange: (open: boolean) => void;
  /** Subcategories to render — typically the result of `useSubcategories(parentId)`. */
  subcategories: ProductSubcategory[];
  /** Called with the picked subcategory id. The dialog auto-closes afterwards. */
  onSelect: (subcategoryId: number) => void;
  /** When true, show the loading state instead of the (likely-stale) list. */
  loading?: boolean;
}

export function SelectSubcategoryDialog({
  open,
  onOpenChange,
  subcategories,
  onSelect,
  loading = false,
}: SelectSubcategoryDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Select subcategory</DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          {loading && (
            <p className="text-sm text-muted-foreground">Loading…</p>
          )}
          {!loading && subcategories.length === 0 && (
            <p className="text-sm text-muted-foreground">No subcategories</p>
          )}
          {!loading &&
            subcategories.map((s) => (
              <button
                key={s.id}
                type="button"
                className="w-full rounded border border-border p-3 text-left text-sm hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                onClick={() => {
                  onSelect(s.id);
                  onOpenChange(false);
                }}
              >
                {s.label}
              </button>
            ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
